/**
 * Glyph decoration in PSD layer effects. Photoshop repaints from the effects when the type layout is edited.
 */

import type { BlendMode, LayerEffectShadow, LayerEffectsInfo } from "ag-psd";

import type {
  TextLayer,
  TextShadow,
} from "@hightouch/lib/ad-studio/html-editor/model";

import { parseCssLength } from "@hightouch/lib/ad-studio/psd-export/css-length";
import { parseCssColor } from "@hightouch/lib/ad-studio/psd-export/psd-color";

/**
 * Without `present`/`showInDialog` Photoshop abandons the whole effects block ("problems were
 * encountered reading layers"). ag-psd emits only the keys it is handed.
 */
const EFFECT_PRESENCE = {
  enabled: true,
  present: true,
  showInDialog: true,
} as const;

/** Photoshop's default transfer curve. An empty curve is not a valid contour. */
const LINEAR_CONTOUR = {
  name: "Linear",
  curve: [
    { x: 0, y: 0 },
    { x: 255, y: 255 },
  ],
};

const TEXT_STROKE_KEY = "-webkit-text-stroke";
const TEXT_STROKE_WIDTH_KEY = "-webkit-text-stroke-width";
const TEXT_STROKE_COLOR_KEY = "-webkit-text-stroke-color";
const PAINT_ORDER_KEY = "paint-order";

export function buildTextLayerEffects(
  layer: TextLayer,
): LayerEffectsInfo | undefined {
  const dropShadow = layer.textShadow.map(psdShadow);
  const stroke = psdTextStroke(layer);
  if (dropShadow.length === 0 && !stroke) return undefined;
  return {
    ...(dropShadow.length > 0 ? { dropShadow } : {}),
    ...(stroke ? { stroke: [stroke] } : {}),
  };
}

/**
 * CSS offsets are cartesian; PSD's are polar off an angle naming where the *light* is, so the
 * direction is `angle + 180°`. `useGlobalLight` off, or every shadow swings to one direction.
 */
function psdShadow(shadow: TextShadow): LayerEffectShadow {
  const { color, alpha } = parseCssColor(shadow.color);
  const distance = Math.hypot(shadow.x, shadow.y);
  return {
    ...EFFECT_PRESENCE,
    color,
    opacity: alpha,
    blendMode: "normal" as BlendMode,
    useGlobalLight: false,
    // Undefined at zero distance, where any angle renders the same.
    angle:
      distance === 0 ? 0 : (Math.atan2(shadow.y, -shadow.x) * 180) / Math.PI,
    distance: { units: "Pixels", value: distance },
    // CSS blur is a Gaussian diameter, PSD size a spread radius; within a pixel at ad sizes.
    size: { units: "Pixels", value: shadow.blur },
    choke: { units: "Pixels", value: 0 },
    antialiased: false,
    // CSS paints the shadow behind the glyph, which is what this hides.
    layerConceals: true,
    contour: LINEAR_CONTOUR,
  };
}

/**
 * `-webkit-text-stroke` straddles the glyph outline, matching PSD's centered stroke 1:1 — except
 * under `paint-order: stroke`, where the fill repaints the inner half and only half the width shows.
 */
function psdTextStroke(layer: TextLayer) {
  const styles = layer.extraStyles;
  if (!styles) return undefined;
  const shorthand = parseStrokeShorthand(styles[TEXT_STROKE_KEY]);
  const width =
    parseCssLength(styles[TEXT_STROKE_WIDTH_KEY]) ?? shorthand.width ?? null;
  if (width === null || width <= 0) return undefined;
  const cssColor = styles[TEXT_STROKE_COLOR_KEY] ?? shorthand.color;
  if (!cssColor) return undefined;

  const { color, alpha } = parseCssColor(cssColor);
  const paintsUnderFill = (styles[PAINT_ORDER_KEY] ?? "").includes("stroke");
  return {
    ...EFFECT_PRESENCE,
    position: paintsUnderFill ? ("outside" as const) : ("center" as const),
    fillType: "color" as const,
    blendMode: "normal" as BlendMode,
    opacity: alpha,
    color,
    size: {
      units: "Pixels" as const,
      value: paintsUnderFill ? width / 2 : width,
    },
  };
}

/** `-webkit-text-stroke: <width> <color>`, either half optional. */
function parseStrokeShorthand(value: string | undefined): {
  width?: number;
  color?: string;
} {
  if (!value) return {};
  const out: { width?: number; color?: string } = {};
  const colorParts: string[] = [];
  // Split on whitespace outside parentheses so `rgb(0 0 0 / 50%)` stays whole.
  for (const token of splitTopLevel(value)) {
    const px = parseCssLength(token);
    if (px !== null && out.width === undefined) out.width = px;
    else colorParts.push(token);
  }
  if (colorParts.length > 0) out.color = colorParts.join(" ");
  return out;
}

function splitTopLevel(value: string): string[] {
  const tokens: string[] = [];
  let depth = 0;
  let current = "";
  for (const char of value.trim()) {
    if (char === "(") depth++;
    if (char === ")") depth--;
    if (depth === 0 && /\s/.test(char)) {
      if (current) tokens.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  if (current) tokens.push(current);
  return tokens;
}
